import type { PayloadAction } from '@reduxjs/toolkit'
import type { CaseReducer } from '@reduxjs/toolkit/dist/createReducer'
import type { ActionReducerMapBuilder, TypedActionCreator } from '@reduxjs/toolkit/src/mapBuilders'
import { toArray } from 'packages/helper'

type PayloadActionReject<ReturnAction> = ReturnAction extends PayloadAction<
  unknown,
  infer Type,
  infer Meta,
  infer Error
>
  ? PayloadAction<any, Type, Meta, Error>
  : ReturnAction

type ReturnTypeAction<T extends undefined | TypedActionCreator<string>> = T extends undefined
  ? never
  : T extends (...args: any) => infer R
  ? PayloadAction<unknown, any, any, any> extends R
    ? PayloadActionReject<R>
    : R
  : any

type ActionCreatorType = undefined | TypedActionCreator<string> | (undefined | TypedActionCreator<string>)[]

export type SliceAction<State, ActionCreator extends ActionCreatorType = ActionCreatorType> = {
  action?: ActionCreator
  reducer?: CaseReducer<
    State,
    ActionCreator extends (undefined | TypedActionCreator<string>)[]
      ? ReturnTypeAction<ActionCreator[0]>
      : ActionCreator extends TypedActionCreator<string>
      ? ReturnTypeAction<ActionCreator>
      : never
  >
}

type Builder<State> = Omit<ActionReducerMapBuilder<State>, 'addCase'> & {
  addCase<ActionCreator extends ActionCreatorType>(
    actions?: ActionCreator,
    reducer?: CaseReducer<
      State,
      ActionCreator extends (undefined | TypedActionCreator<string>)[]
        ? ReturnTypeAction<ActionCreator[0]>
        : ActionCreator extends TypedActionCreator<string>
        ? ReturnTypeAction<ActionCreator>
        : never
    >,
  ): Builder<State>
  addSlice<ActionCreator extends ActionCreatorType, SliceState extends Partial<State>>(
    sliceActions: SliceAction<SliceState, ActionCreator>[],
  ): Builder<State>
}

const addSliceCases = <State, SliceState extends Partial<State>>(
  newBuilder: Builder<State>,
  sliceActions: SliceAction<SliceState>[],
  i = 0,
): Builder<State> => {
  const indexAction = i - 1
  if (indexAction < 0) {
    return newBuilder
  }
  if (i > 0) {
    return addSliceCases<State, SliceState>(
      newBuilder.addCase(
        sliceActions[indexAction].action,
        (sliceActions[indexAction] as unknown as SliceAction<State>).reducer,
      ),
      sliceActions,
      i - 1,
    )
  }
  return newBuilder.addCase(
    sliceActions[indexAction].action,
    (sliceActions[indexAction] as unknown as SliceAction<State>).reducer,
  )
}

export const createBuilder = <State>(builder: ActionReducerMapBuilder<State>) => {
  const newBuilder = builder as Builder<State>
  const { addCase } = builder

  newBuilder.addCase = (actions, reducer) => {
    if (!actions || !reducer) {
      return newBuilder
    }
    toArray(actions).forEach((action) => {
      if (action) {
        addCase(action, reducer as CaseReducer<State, ReturnType<TypedActionCreator<string>>>)
      }
    })
    return newBuilder
  }

  newBuilder.addSlice = (sliceActions) => {
    if (!sliceActions?.length) {
      return newBuilder
    }
    return addSliceCases(newBuilder, sliceActions, sliceActions.length)
  }

  return newBuilder
}
